import Bugsnag from '@bugsnag/js';
import { INE_URL } from 'constants/domains';
import {
  FEATURE_NOT_AVAILABLE,
  INVALID_TOKEN,
  NOT_ALLOWED,
  isRetryableError,
} from 'constants/errors/codes';
import { END_USERS_NOT_ALLOWED_MSG, INVALID_TOKEN_REDIRECT_MSG } from 'constants/errors/messages';

interface IServiceError extends Error {
  response;
}

export interface IHandledError extends Error {
  status?: number;
  retryable?: boolean;
  code?: string;
  featureSlug?: string;
}

export const handleErrors = (error: IServiceError): Error | any => {
  // The BE exception handler nests multi-key error bodies under `details` (e.g. the
  // feature_not_available 403 `{ detail, code, feature }`) and only flattens single-key
  // `detail`. Pull the human-readable `detail` out of the object so the error message and
  // any Bugsnag payload stay a string instead of `[object Object]`.
  const rawDetails = error.response?.data?.details;
  const errorMsg =
    rawDetails && typeof rawDetails === 'object'
      ? rawDetails.detail ?? error.response?.data?.detail
      : rawDetails ?? error.response?.data?.detail;
  if (process.env.NODE_ENV !== 'test') {
    // The BE global exception handler wraps error payloads under `details` and only
    // flattens them when `detail` is the only key, so a 3-key PermissionDenied body
    // (detail/code/feature) arrives nested under `details`. Prefer that nested object,
    // falling back to the flat shape for robustness.
    const data = error.response?.data;
    const featurePayload = data?.details && typeof data.details === 'object' ? data.details : data;
    const isFeatureNotAvailable =
      error.response?.status === NOT_ALLOWED && featurePayload?.code === FEATURE_NOT_AVAILABLE;
    if (errorMsg && !isFeatureNotAvailable) {
      Bugsnag.notify(errorMsg);
    }
    const currentUrl = window.location.href;
    if (error.response?.status === INVALID_TOKEN) {
      const redirectUrl = `${INE_URL}/login?redirect_url=${currentUrl}`;
      window.location.replace(`${redirectUrl}&error=${INVALID_TOKEN_REDIRECT_MSG}`);
    } else if (error.response?.status === NOT_ALLOWED && !isFeatureNotAvailable) {
      const redirectUrl = `${INE_URL}/login?redirect_url=${currentUrl}`;
      window.location.replace(`${redirectUrl}&error=${END_USERS_NOT_ALLOWED_MSG}`);
    }
    // Preserve the HTTP status and whether it's worth retrying so pollers/retry loops
    // that catch this error can tell a 5xx apart from a validation error.
    const handledError: IHandledError = new Error(errorMsg);
    handledError.status = error.response?.status;
    handledError.retryable = isRetryableError(error);
    if (isFeatureNotAvailable) {
      handledError.code = featurePayload?.code;
      handledError.featureSlug = featurePayload?.feature;
    }
    throw handledError;
  }
};
